import * as Yup from "yup"

import useIsDesktop from "@hooks/useIsDesktop"
import useRouter from "@hooks/useRouter"

import { AlertBanner, Button, NavigationLink } from "@atoms/index"

import { PasswordInput, PhoneNumberInput } from "@molecules/Form/Form"
import GenericForm from "@molecules/Form/GenericForm/GenericForm"

import styles from "@pages/Auth/Auth.module.scss"
import useAuth from "@pages/Auth/Auth.store"
import { phoneNumberSchema } from "@pages/Auth/AuthPhoneCodeForm/AuthPhoneCodeForm"

import { AUTH_ENDPOINTS } from "@endpoints/auth"

import {
    AuthPhonePasswordErrorResponse,
    PhoneAndPasswordPayload,
    PhoneAndPasswordSchema,
} from "./AuthPhonePasswordForm.types"

const phoneAndPasswordSchema: Yup.ObjectSchema<PhoneAndPasswordSchema> = Yup.object()
    .concat(phoneNumberSchema)
    .shape({
        password: Yup.string().required("Please enter a password"),
    })

export default function AuthPhonePasswordForm() {
    const { authMethod, setAuthMethod, setPhoneData, postAuthRedirectRoute } = useAuth()

    const isDesktop = useIsDesktop()
    const router = useRouter()

    const isActive = authMethod === "phone_and_password"

    const onMutationSuccess = () => {
        router.replace(postAuthRedirectRoute)
    }

    const onRequestCodeInsteadClick = () => {
        void setAuthMethod("phone_and_code")
    }

    const onPhoneChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setPhoneData({
            phone: event.target.value,
            phoneId: null,
        })
    }

    return (
        <GenericForm<PhoneAndPasswordSchema, User, AuthPhonePasswordErrorResponse, PhoneAndPasswordPayload>
            formConfig={{
                defaultValues: {
                    phone_number: "",
                    password: "",
                },
            }}
            formValidationSchema={phoneAndPasswordSchema}
            mutationConfig={{
                method: "POST",
                endpoint: AUTH_ENDPOINTS.PASSWORD.SMS_AUTHENTICATE,
                genericErrorMessage: "Could not log in.",
            }}
            triggerFormReset={!isActive}
            onMutationSuccess={onMutationSuccess}
            className={styles.form}
        >
            {({ isPending, nonFieldError }) => (
                <>
                    {nonFieldError && (
                        <AlertBanner size={isDesktop ? "sm" : "md"} title={nonFieldError} type="error" />
                    )}
                    <PhoneNumberInput<PhoneAndPasswordSchema>
                        name="phone_number"
                        label="Phone number"
                        placeholder="(000) 000-0000"
                        isTabbable={isActive}
                        size={isDesktop ? "md" : "lg"}
                        onChangeCapture={onPhoneChange}
                    />
                    <PasswordInput<PhoneAndPasswordSchema>
                        name="password"
                        label="Password"
                        placeholder="Password"
                        isTabbable={isActive}
                        size={isDesktop ? "md" : "lg"}
                    />

                    <NavigationLink
                        onClick={onRequestCodeInsteadClick}
                        isUnderLine={true}
                        className={styles.link}
                        isTabbable={isActive}
                        title="Email and code form"
                        data-prevent-input-validation={true}
                    >
                        {() => "Use code instead"}
                    </NavigationLink>
                    <Button
                        type="submit"
                        colorScheme="gray"
                        size={isDesktop ? "md" : "lg"}
                        variant="solid"
                        isFullWidth={true}
                        isTabbable={isActive}
                        isLoading={isPending}
                    >
                        Sign in
                    </Button>
                </>
            )}
        </GenericForm>
    )
}
